import type { Types } from '@ohif/core';
import React from 'react';
import {
  VIEWPORT_OVERLAY_BOTTOM_LEFT,
  VIEWPORT_OVERLAY_TOP_LEFT,
} from './components/overlay/viewportOverlayItems';
import DicomUpload from './components/DicomUpload';
import DeleteAction from './components/WorklistDelete';
import SycaiLoadingIndicator from './components/SycaiLoadingIndicator';
import SycaiLoadingIndicatorTotalPercent from './components/SycaiLoadingIndicatorTotalPercent';
import { isDownloadableDisplaySet } from './utils/isDownloadableDisplaySet';

export const SYCAI_DIAGNOSIS_BUTTON = {
  id: 'SycaiDiagnosis',
  uiType: 'sycai.diagnosisButton',
  props: {
    tooltip: 'Run the SYCAI diagnosis for this study',
  },
};

export const SYCAI_ORGAN_REVIEW_BUTTON = {
  id: 'SycaiOrganReview',
  uiType: 'sycai.organReviewButton',
  props: {
    tooltip: 'Review organ image quality for this series',
  },
};

export default function getCustomizationModule(
  { servicesManager }: Types.Extensions.ExtensionParams = {} as Types.Extensions.ExtensionParams
) {
  return [
    {
      name: 'global',
      value: {
        'sycai.marker': 'm1',
        'cornerstone.toolbarButtons': {
          $push: [SYCAI_DIAGNOSIS_BUTTON, SYCAI_ORGAN_REVIEW_BUTTON],
        },
        'cornerstone.toolbarSections': {
          primary: { $push: ['SycaiDiagnosis', 'SycaiOrganReview'] },
        },
      },
    },
    {
      name: 'default',
      value: {
        'viewportOverlay.topLeft': VIEWPORT_OVERLAY_TOP_LEFT,
        'viewportOverlay.bottomLeft': VIEWPORT_OVERLAY_BOTTOM_LEFT,

        dicomUploadComponent: DicomUpload,

        'ui.loadingIndicatorProgress': SycaiLoadingIndicator,

        'ui.loadingIndicatorTotalPercent': SycaiLoadingIndicatorTotalPercent,

        'workList.columns': {
          $apply: (cols: any[]) =>
            cols.map(col => {
              if (col.id !== 'actions') {
                return col;
              }
              return {
                ...col,
                cell: (context: any) => (
                  <DeleteAction
                    cell={context.cell}
                    table={context.table}
                  />
                ),
              };
            }),
        },

        'workList.settingsMenuItems': (
          defaults: Array<{ id: string; label: React.ReactNode; onClick: () => void }>
        ) => [
          ...defaults,
          {
            id: 'sycaiLogout',
            label: 'Log out',
            onClick: () => servicesManager.services.sessionService.logOut(),
          },
        ],

        'ohif.logoutHandler': () => servicesManager.services.sessionService.logOut(),
        'ohif.currentUsername': () =>
          servicesManager.services.sessionService.getAccount()?.username,

        onBeforeSRAddMeasurement: {
          $apply:
            (upstreamOnBeforeSRAddMeasurement: (args: any) => any) =>
            (args: { measurement: any; StudyInstanceUID: string; SeriesInstanceUID: string }) => {
              const { measurement } = args;
              if (!measurement.coords?.length) {
                measurement.coords = [{}];
              }
              return upstreamOnBeforeSRAddMeasurement(args);
            },
        },

        'studyBrowser.thumbnailMenuItems': {
          $push: [
            {
              id: 'downloadSeries',
              label: 'Download',
              iconName: 'Download',
              commands: 'downloadSeries',
            },
          ],
        },

        'ohif.menuContent': {
          $apply: (upstreamMenuContent: (props: any) => JSX.Element) =>
            function sycaiMenuContent(props: any) {
              const { item, servicesManager, ...rest } = props;

              if (
                item.id === 'downloadSeries' &&
                !isDownloadableDisplaySet(
                  rest.displaySetInstanceUID,
                  servicesManager.services.displaySetService
                )
              ) {
                return null;
              }

              return upstreamMenuContent(props);
            },
        },
      },
    },
  ];
}
